import {
  Badge,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ChatBottomNav from '../components/chat/ChatBottomNav';
import AppHeader from '../components/layout/AppHeader';
import Toast from '../components/Toast';
import { API_ENDPOINTS } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';

interface Habit {
  id: string;
  title: string;
  description: string;
  category: string;
  category_display: string;
  target_timeframe: string;
  status: string;
  status_display: string;
  priority: number;
  action_count: number;
  average_score: number;
  current_streak: number;
  created_at: string;
  activated_at: string | null;
}

type FilterStatus = 'active' | 'suggested' | 'paused';

const STATUS_COLORS: Record<string, string> = {
  suggested: '#17a2b8',
  active: '#4CAF50',
  paused: '#FFC107',
  completed: '#6c757d',
  dismissed: '#dc3545',
};

const CATEGORY_ICONS: Record<string, string> = {
  mental_health: '🧠',
  physical_health: '💪',
  nutrition: '🥗',
  sleep: '😴',
  productivity: '📈',
  relationships: '❤️',
  financial: '💰',
  mindfulness: '🧘',
  exercise: '🏃',
  self_care: '💆',
  learning: '📚',
  social: '👥',
  general: '✨',
};

const HabitsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [habits, setHabits] = useState<Habit[]>([]);
  const [allHabits, setAllHabits] = useState<{ active: Habit[]; suggested: Habit[]; paused: Habit[] }>({ active: [], suggested: [], paused: [] });
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('active');
  const { toast, showError, closeToast } = useToast();

  const loadHabits = useCallback(async () => {
    try {
      setLoading(true);
      const url = `${API_ENDPOINTS.HABITS.LIST}?status=${filterStatus}`;
      const res = await axios.get(url);
      const results: Habit[] = res.data?.results ?? res.data ?? [];
      setHabits(Array.isArray(results) ? results : []);

      // Load counts for all statuses
      const [activeRes, suggestedRes, pausedRes] = await Promise.all([
        axios.get(`${API_ENDPOINTS.HABITS.LIST}?status=active`),
        axios.get(`${API_ENDPOINTS.HABITS.LIST}?status=suggested`),
        axios.get(`${API_ENDPOINTS.HABITS.LIST}?status=paused`),
      ]);
      setAllHabits({
        active: activeRes.data?.results ?? activeRes.data ?? [],
        suggested: suggestedRes.data?.results ?? suggestedRes.data ?? [],
        paused: pausedRes.data?.results ?? pausedRes.data ?? [],
      });
    } catch (e: any) {
      showError(e?.response?.data?.detail || e?.message || 'Failed to load habits');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    loadHabits();
  }, [loadHabits]);

  const handleFilterChange = (
    _event: React.MouseEvent<HTMLElement>,
    newFilter: FilterStatus | null
  ) => {
    if (newFilter !== null) {
      setFilterStatus(newFilter);
    }
  };

  const getScoreColor = (score: number): string => {
    if (score >= 4) return '#4CAF50';
    if (score >= 3) return '#FFC107';
    return '#dc3545';
  };

  const renderScoreCircle = (score: number) => {
    const color = getScoreColor(score);
    const percentage = (score / 5) * 100;

    return (
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <CircularProgress
          variant="determinate"
          value={100}
          size={60}
          thickness={4}
          sx={{ color: '#e0e0e0' }}
        />
        <CircularProgress
          variant="determinate"
          value={percentage}
          size={60}
          thickness={4}
          sx={{
            color: color,
            position: 'absolute',
            left: 0,
          }}
        />
        <Box
          sx={{
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 700, color }}>
            {score.toFixed(1)}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ height: 'var(--app-height, 100vh)', display: 'flex', flexDirection: 'column', bgcolor: '#F5F5F5' }}>
      <AppHeader title="Habits" />

      <Box sx={{ p: 2, bgcolor: '#fff', borderBottom: '1px solid #e0e0e0' }}>
        <ToggleButtonGroup
          value={filterStatus}
          exclusive
          onChange={handleFilterChange}
          aria-label="habit filter"
          size="small"
          sx={{ width: '100%', display: 'flex' }}
        >
          <ToggleButton value="active" sx={{ flex: 1, position: 'relative' }}>
            Active
            {allHabits.active.length > 0 && (
              <Badge
                badgeContent={allHabits.active.length}
                sx={{
                  position: 'absolute',
                  top: 4,
                  right: 8,
                  '& .MuiBadge-badge': {
                    bgcolor: '#2196F3',
                    color: '#fff',
                    fontSize: '0.65rem',
                    height: 18,
                    minWidth: 18,
                    borderRadius: '9px',
                  },
                }}
              />
            )}
          </ToggleButton>
          <ToggleButton value="suggested" sx={{ flex: 1, position: 'relative' }}>
            Suggested
            {allHabits.suggested.length > 0 && (
              <Badge
                badgeContent={allHabits.suggested.length}
                sx={{
                  position: 'absolute',
                  top: 4,
                  right: 8,
                  '& .MuiBadge-badge': {
                    bgcolor: '#2196F3',
                    color: '#fff',
                    fontSize: '0.65rem',
                    height: 18,
                    minWidth: 18,
                    borderRadius: '9px',
                  },
                }}
              />
            )}
          </ToggleButton>
          <ToggleButton value="paused" sx={{ flex: 1, position: 'relative' }}>
            Paused
            {allHabits.paused.length > 0 && (
              <Badge
                badgeContent={allHabits.paused.length}
                sx={{
                  position: 'absolute',
                  top: 4,
                  right: 8,
                  '& .MuiBadge-badge': {
                    bgcolor: '#2196F3',
                    color: '#fff',
                    fontSize: '0.65rem',
                    height: 18,
                    minWidth: 18,
                    borderRadius: '9px',
                  },
                }}
              />
            )}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', p: 2, bgcolor: '#f5f5f5' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : habits.length === 0 ? (
          <Box sx={{ textAlign: 'center', mt: 4, color: '#666' }}>
            <Typography variant="h1" sx={{ fontSize: '3rem', mb: 2 }}>🎯</Typography>
            <Typography variant="h6">No habits yet</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {filterStatus === 'suggested'
                ? "Chat with Jeff and we'll suggest habits based on your conversations!"
                : "Your habits will appear here once you start building them."}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {habits.map((habit) => (
              <Grid item xs={12} md={6} lg={4} key={habit.id}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    transition: 'box-shadow 0.2s',
                    '&:hover': { boxShadow: 4 },
                  }}
                  onClick={() => navigate(`/habits/${habit.id}`)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontSize: '1.2rem' }}>
                          {CATEGORY_ICONS[habit.category] || '✨'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {habit.category_display}
                        </Typography>
                      </Box>
                      <Chip
                        label={habit.status_display}
                        size="small"
                        sx={{
                          bgcolor: STATUS_COLORS[habit.status] || '#6c757d',
                          color: '#fff',
                          fontWeight: 600,
                          fontSize: '0.7rem',
                        }}
                      />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, mb: 0.5 }}>
                          {habit.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            mb: 1.5,
                            minHeight: '2.5em',
                          }}
                        >
                          {habit.description}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 3 }}>
                          <Box>
                            <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 700 }}>
                              {habit.action_count}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Actions
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 700 }}>
                              {habit.current_streak}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Day Streak
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {renderScoreCircle(habit.average_score)}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      <Toast
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={closeToast}
      />

      <ChatBottomNav />
    </Box>
  );
};

export default HabitsPage;


